import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { CarouselJsonLd } from 'next-seo'
import { FiHeadphones } from '@react-icons/all-files/fi/FiHeadphones'
import { FiTag } from '@react-icons/all-files/fi/FiTag'
import { FiTruck } from '@react-icons/all-files/fi/FiTruck'
import { loadEntityForce } from '../../../store/slices/entities/crmActions'
import {
  getAllFilteredProducts,
  getProductsFilters,
  productsResetFilters
} from '../../../store/slices/entities/products'
import { apiCallBegan } from '../../../store/api'
import { cartAddItem, cartCleaned, getUser, getUserGeo } from '../../../store/slices/user'
import { getWorkingOrder, workingOrderReset } from '../../../store/slices/entities/orders'
import { CLOSE_POPUP, showAlert, SHOW_POPUP } from '../../../store/slices/ui'
import { getWorkingPackage } from '../../../store/slices/entities/packages'
import { helper } from '../../../utilities/helper'
import { resolveOrderProduct } from '../../../utilities/orderProductHydration'
import * as gtag from '../../../lib/gtag'
import Container from '../foundations/Container'
import UiBoundary from '../foundations/UiBoundary'
import Action from '../primitives/Action'
import ProductRow from './ProductRow'
import RentalStartPopup from './RentalStartPopup'
import styles from './ProductCatalog.module.scss'
import { useOrderFlow } from '../../unified-order/OrderFlowProvider'
import { getCatalogMarketPresentation, isProductAvailableInMarket, normalizeMarket, requiresMiamiFulfillment, resolveShoppingMarket } from '../../../utilities/fulfillmentMarket'
import { compareCatalogRelevance } from '../../../utilities/catalogProductOrder'
import { observeVisibleCatalogItems } from '../../../utilities/visibleCatalogImpressions'

const sortValue = (product, label) => product.snippets
  ?.find(snippet => snippet.includes(label))
  ?.split('//')?.[1]

const numericSnippet = (product, label) => Number.parseFloat(sortValue(product, label)) || 0

const sortCatalogProducts = (products, sort, orlando, relevanceOrder = []) => {
  const quality = ['Standard', 'Premium', 'Top Quality']
  const djLevel = ['Intermediate', 'Professional']

  return [...products].sort((a, b) => {
    if (sort === 'Relevance' && !orlando) return compareCatalogRelevance(a, b, relevanceOrder)
    if (sort === 'price-hl') return (b.price?.rental || 0) - (a.price?.rental || 0)
    if (sort === 'price-lh') return (a.price?.rental || 0) - (b.price?.rental || 0)
    if (sort === 'Sound Quality') return quality.indexOf(sortValue(b, sort)) - quality.indexOf(sortValue(a, sort))
    if (sort === 'DJ Level') return djLevel.indexOf(sortValue(b, sort)) - djLevel.indexOf(sortValue(a, sort))
    if (sort === 'Mic Type' || sort === 'Screen Type') return String(sortValue(b, sort) || '').length - String(sortValue(a, sort) || '').length
    if (sort === 'Pitch') return numericSnippet(a, sort) - numericSnippet(b, sort)
    if (orlando && sort === 'Relevance') {
      const bLocal = isProductAvailableInMarket(b, 'Orlando') ? 1 : 0
      const aLocal = isProductAvailableInMarket(a, 'Orlando') ? 1 : 0
      return bLocal - aLocal || compareCatalogRelevance(a, b, relevanceOrder)
    }
    return numericSnippet(b, sort) - numericSnippet(a, sort)
  })
}

const paginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index)

  const candidates = new Set([0, totalPages - 1, currentPage - 1, currentPage, currentPage + 1])
  const pages = [...candidates]
    .filter(index => index >= 0 && index < totalPages)
    .sort((a, b) => a - b)

  return pages.reduce((items, pageIndex, index) => {
    if (index && pageIndex - pages[index - 1] > 1) items.push(`ellipsis-${pageIndex}`)
    items.push(pageIndex)
    return items
  }, [])
}

const ProductCatalog = ({
  products = [],
  listName,
  filters: filterOptions = [],
  sortOptions = [],
  showAssurances = true,
  carousel,
  anchor = false,
  anchorId,
  className = '',
  eyebrow = 'Browse rental equipment',
  description,
  headerAction,
  pageSize = 10,
  showProductDirectory = true,
  actionMode = 'rental',
  supportingLabel,
  supportingDescription,
  supportingActionLabel,
  priceSummaryMode,
  inventoryMarket,
  relevanceOrder = [],
  impressionMode = 'rendered',
  headerDensity = 'default',
  emptyState
}) => {
  const [activeFilter, setActiveFilter] = useState('')
  const [page, setPage] = useState(0)
  const [sort, setSort] = useState('Relevance')
  const dispatch = useDispatch()
  const catalogRef = useRef()
  const impressionRef = useRef('')
  const productListRef = useRef()
  const visibleImpressionsRef = useRef({})
  const reactId = useId()
  const { pathname } = useRouter()
  const productFilters = useSelector(getProductsFilters)
  const allProducts = useSelector(getAllFilteredProducts)
  const { cart } = useSelector(getUser)
  const geo = useSelector(getUserGeo)
  const order = useSelector(getWorkingOrder)
  const workingPackage = useSelector(getWorkingPackage)
  const { launchIntent, unifiedEnabled } = useOrderFlow()
  const shoppingMarket = resolveShoppingMarket({geo, order, pathname})
  const catalogMarket = inventoryMarket ? normalizeMarket(inventoryMarket) : shoppingMarket
  const marketPresentation = getCatalogMarketPresentation(catalogMarket)
  const orlando = catalogMarket === 'Orlando'
  const [inventoryScope, setInventoryScope] = useState(orlando ? 'orlando' : 'all')
  const unifiedEligible = unifiedEnabled && !workingPackage && !order?._id && !cart.items?.length
  const safeListName = listName || 'Equipment Catalog'
  const catalogSlug = helper.makeUrl(safeListName) || 'equipment-catalog'
  const instanceSuffix = reactId.replace(/[^a-zA-Z0-9_-]/g, '') || 'catalog'
  const catalogId = `${catalogSlug}-${instanceSuffix}`
  const titleId = `${catalogId}-title`
  const filterLabelId = `${catalogId}-filters-label`
  const resultStatusId = `${catalogId}-results`
  const resolvedAnchorId = anchorId || (anchor ? 'productsAnchor' : undefined)
  const normalizedPageSize = Math.max(1, Number(pageSize) || 10)

  const filteredProducts = useMemo(() => {
    const filterValue = String(activeFilter || '').toLowerCase()
    const visible = products.filter(product => {
      const matchesFilter = !activeFilter
        || product.name?.toLowerCase().includes(filterValue)
        || product.tags?.some(tag => String(tag).toLowerCase() === filterValue)
      const matchesWarehouse = !orlando
        || inventoryScope === 'all'
        || isProductAvailableInMarket(product, 'Orlando')
      return matchesFilter && matchesWarehouse && (orlando || !product.only_orlando)
    })
    return sortCatalogProducts(visible, sort, orlando, relevanceOrder)
  }, [activeFilter, inventoryScope, orlando, products, relevanceOrder, sort])

  const pages = Math.ceil(filteredProducts.length / normalizedPageSize)
  const pageProducts = useMemo(() => (
    filteredProducts.slice(page * normalizedPageSize, (page * normalizedPageSize) + normalizedPageSize)
  ), [filteredProducts, normalizedPageSize, page])
  const visiblePageItems = paginationItems(page, pages)
  const firstVisibleItem = filteredProducts.length ? (page * normalizedPageSize) + 1 : 0
  const lastVisibleItem = Math.min((page + 1) * normalizedPageSize, filteredProducts.length)
  const rentalPrices = filteredProducts
    .filter(product => !product.only_purchase && Number.isFinite(Number(product.price?.rental)))
    .map(product => Number(product.price.rental))
  const minimumRentalPrice = actionMode === 'rental' && rentalPrices.length
    ? Math.min(...rentalPrices)
    : null
  const isCompleteBundle = product => Boolean(product.bundle || /\bbundle\b/i.test(product.name || ''))
  const componentRentalPrices = filteredProducts
    .filter(product => !isCompleteBundle(product) && !product.only_purchase && Number.isFinite(Number(product.price?.rental)))
    .map(product => Number(product.price.rental))
  const bundleRentalPrices = filteredProducts
    .filter(product => isCompleteBundle(product) && !product.only_purchase && Number.isFinite(Number(product.price?.rental)))
    .map(product => Number(product.price.rental))
  const componentMinimumRentalPrice = componentRentalPrices.length ? Math.min(...componentRentalPrices) : null
  const bundleMinimumRentalPrice = bundleRentalPrices.length ? Math.min(...bundleRentalPrices) : null

  useEffect(() => {
    setPage(0)
  }, [activeFilter, inventoryScope, products, sort])

  useEffect(() => {
    setInventoryScope(orlando ? 'orlando' : 'all')
  }, [orlando])

  useEffect(() => {
    if (!pageProducts.length || impressionMode === 'visible') return
    const impressionKey = [
      catalogId,
      activeFilter || 'all',
      sort,
      page,
      ...pageProducts.map(product => product._id)
    ].join('|')
    if (impressionRef.current === impressionKey) return
    impressionRef.current = impressionKey
    gtag.event({
      action: 'view_item_list',
      category: 'orders',
      filterState: activeFilter || 'all',
      itemListId: catalogId,
      itemListName: safeListName,
      label: 'sales',
      pageNumber: page + 1,
      resultCount: filteredProducts.length,
      sortState: sort,
      sourceSurface: `product_catalog:${safeListName}`,
      items: pageProducts.map((product, index) => ({
        item_id: product._id,
        item_name: product.name,
        index: (page * normalizedPageSize) + index,
        item_category: product.categories?.[0]?.split('/')?.[0],
        item_category2: product.categories?.[0]?.split('/')?.[1],
        price: product.price?.rental,
        quantity: 1
      }))
    })
  }, [activeFilter, catalogId, filteredProducts.length, impressionMode, normalizedPageSize, page, pageProducts, safeListName, sort])

  useEffect(() => {
    if (impressionMode !== 'visible' || !pageProducts.length) return undefined
    const viewKey = [catalogMarket, activeFilter || 'all', sort, page].join('|')
    const measured = visibleImpressionsRef.current[viewKey] || new Set()
    visibleImpressionsRef.current[viewKey] = measured
    return observeVisibleCatalogItems({
      root: productListRef.current,
      measured,
      onVisible: ids => gtag.event({
        action: 'view_item_list',
        category: 'orders',
        label: 'sales',
        market: orlando ? 'orlando' : 'miami_south_florida',
        filterState: activeFilter || 'all',
        itemListId: catalogId,
        itemListName: safeListName,
        pageNumber: page + 1,
        resultCount: filteredProducts.length,
        sortState: sort,
        sourceSurface: `product_catalog:${safeListName}`,
        items: pageProducts.flatMap((product, index) => ids.includes(String(product._id)) ? [{
          item_id: product._id,
          item_name: product.name,
          index: (page * normalizedPageSize) + index,
          item_category: product.categories?.[0]?.split('/')?.[0],
          item_category2: product.categories?.[0]?.split('/')?.[1],
          price: product.price?.rental,
          quantity: 1
        }] : [])
      })
    })
  }, [activeFilter, catalogId, catalogMarket, filteredProducts.length, impressionMode, normalizedPageSize, orlando, page, pageProducts, safeListName, sort])

  const selectPage = nextPage => {
    setPage(nextPage)
    catalogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const trackDirectorySelection = (product, itemIndex) => gtag.event({
    action: 'select_item',
    category: 'orders',
    filterState: activeFilter || 'all',
    itemIndex,
    itemListId: catalogId,
    itemListName: safeListName,
    label: 'sales',
    productId: product._id,
    productName: product.name,
    sortState: sort,
    sourceSurface: `product_catalog_directory:${safeListName}`,
    items: [{
      item_id: product._id,
      item_name: product.name,
      index: itemIndex,
      item_category: product.categories?.[0]?.split('/')?.[0],
      item_category2: product.categories?.[0]?.split('/')?.[1],
      price: product.price?.rental,
      quantity: 1
    }]
  })

  const loadOrderProduct = async (requestedProduct, showSpinner = true) => {
    let loadedProducts
    if (productFilters.static) {
      loadedProducts = await dispatch(loadEntityForce('products', null, null, showSpinner))
      dispatch(productsResetFilters())
    }

    const orderProducts = Array.isArray(loadedProducts) ? loadedProducts : allProducts
    const currentProduct = resolveOrderProduct({
      requestedProduct,
      loadedProducts,
      cachedProducts: allProducts
    })

    return { currentProduct, orderProducts }
  }

  const addToCartItems = async (product, delivery) => {
    const units = 1

    if (unifiedEligible && !product.only_purchase && !product.price?.map) {
      return launchIntent({
        kind: 'product',
        entityId: product._id,
        entityName: product.name,
        sourceSurface: `product_catalog:${listName || pathname}`,
        fulfillment: delivery ? 'delivery_setup' : 'pickup'
      })
    }

    if (order?.request_type === 'purchase' && !product.price.map) {
      if (!window.confirm('This product is only available for rent. Do you want to create a new rental order?')) return
      return dispatch(apiCallBegan({
        url: `/order/${order._id}`,
        method: 'DELETE',
        onSuccess: workingOrderReset.type,
        onSuccessFunc: () => cartCleaned()
      }))
    }

    const { currentProduct, orderProducts } = await loadOrderProduct(product)
    if (!currentProduct) {
      return dispatch(showAlert({
        type: 'error',
        alert: `${product.name} could not be loaded. Please refresh and try again.`
      }))
    }

    if (delivery) {
      if (orlando && requiresMiamiFulfillment(currentProduct, units)) {
        if (!window.confirm(`${currentProduct.name} is available from our Miami warehouse, not the Orlando warehouse, for these dates. Continuing will create or convert the order to Miami fulfillment and may add a travel fee. Continue?`)) return
      }

      if (!cart.production && order?._id) {
        await dispatch(apiCallBegan({
          url: `/order/${order._id}`,
          method: 'DELETE',
          onSuccess: workingOrderReset.type,
          showSpinner: true
        }))
      }

      return dispatch(cartAddItem({
        product: currentProduct,
        units,
        category: product.categories[0].split('/')[1],
        delivery: true
      }))
    }

    const bundleUnavailable = currentProduct.bundle && currentProduct.included?.some(item => (
      orderProducts.find(candidate => String(candidate._id) === String(item.product._id))?.available
      - (units * item.units) < 0
    ))
    if (order?._id && units > 0 && (currentProduct.available - units < 0 || bundleUnavailable)) {
      return dispatch(showAlert({
        type: 'error',
        alert: `${product.name} has no more units available for the selected date`
      }))
    }

    if (!cart.production && (product.requires_tech || !product.allow_pickup)) {
      if (!window.confirm(`${product.name} requires installation by a Miami Sound Rental technician. Continue?`)) return
      if (order?._id) {
        await dispatch(apiCallBegan({
          url: `/order/${order._id}`,
          method: 'DELETE',
          onSuccess: workingOrderReset.type
        }))
      }
    }

    return dispatch(cartAddItem({
      product: currentProduct,
      units,
      category: product.categories[0].split('/')[1]
    }))
  }

  const openRentalStart = (product, trackingContext = {}) => {
    if (order?._id && order?.request_type === 'rental' && !cart.production) {
      return addToCartItems(product)
    }

    gtag.event({
      action: 'rent_online_cta_click',
      label: 'sales',
      value: 5,
      category: 'orders',
      ...trackingContext
    })

    dispatch(SHOW_POPUP({
      key: `rentalStart-${product._id}`,
      size: 'xl',
      className: 'rentalStartShell',
      trapFocus: true,
      ariaLabelledBy: 'rental-start-title',
      body: (
        <RentalStartPopup
          product={product}
          orlando={orlando}
          selectionOnly={unifiedEligible}
          resolveProduct={unifiedEligible ? undefined : async () => {
            const { currentProduct } = await loadOrderProduct(product, false)
            if (!currentProduct) {
              dispatch(showAlert({
                type: 'error',
                alert: `${product.name} could not be loaded. Please refresh and try again.`
              }))
              return null
            }
            gtag.event({
              action: 'add_to_cart',
              label: 'sales',
              value: currentProduct.price?.rental,
              category: 'orders',
              currency: 'USD',
              ...trackingContext,
              items: [{
                item_id: currentProduct._id,
                item_name: currentProduct.name,
                index: trackingContext.itemIndex || 0,
                item_category: currentProduct.categories?.[0]?.split('/')?.[0],
                item_category2: currentProduct.categories?.[0]?.split('/')?.[1],
                price: currentProduct.price?.rental,
                quantity: 1
              }]
            })
            return currentProduct
          }}
          onSelect={async (delivery, selectedProduct) => {
            gtag.event({
              action: delivery ? 'rent_online_delivery_selected' : 'rent_online_pickup_selected',
              label: 'sales',
              value: 5,
              category: 'orders',
              ...trackingContext
            })
            if (unifiedEligible) {
              dispatch(CLOSE_POPUP({ all: true }))
              await addToCartItems(selectedProduct, delivery)
              return false
            }
            if (delivery && orlando && requiresMiamiFulfillment(selectedProduct)) {
              if (!window.confirm(`${selectedProduct.name} is available from our Miami warehouse, not the Orlando warehouse. Continuing will create a Miami-fulfilled order and may add a travel fee. Continue?`)) return false
            }
            return true
          }}
        />
      )
    }))
  }

  return (
    <UiBoundary
      as='section'
      className={`${styles.section} ${className}`.trim()}
      id={resolvedAnchorId}
      aria-labelledby={titleId}
      data-catalog-id={catalogId}
    >
      <Container>
        <div className={`${styles.header} ${headerDensity === 'compact' ? styles.compactHeader : ''}`.trim()} ref={catalogRef}>
          <div className={styles.headingCopy}>
            <span className={styles.eyebrow}>{eyebrow}</span>
            <h2 id={titleId}>{safeListName}</h2>
            {description && <p className={styles.description}>{description}</p>}
            {headerAction?.label && (
              <Action
                dataConversionChannel={headerAction.channel}
                dataConversionKey={headerAction.conversionKey}
                dataConversionLocation='catalog-header'
                dataConversionSalesPath={headerAction.salesPath}
                external={headerAction.external}
                href={headerAction.href}
                icon={headerAction.icon}
                onClick={headerAction.onClick}
                size='compact'
                variant='secondary'
                className={styles.headerAction}
              >
                {headerAction.label}
              </Action>
            )}
          </div>

          <div className={styles.controls}>
            {(filterOptions.length > 0 || orlando) && (
              <div className={styles.filters} role='group' aria-labelledby={filterLabelId}>
                <span className={styles.visuallyHidden} id={filterLabelId}>Filter {safeListName}</span>
                <Action
                  variant='plain'
                  wrapContent={false}
                  className={!activeFilter && inventoryScope === 'all' ? styles.selected : ''}
                  ariaPressed={!activeFilter && inventoryScope === 'all'}
                  onClick={() => {
                    setInventoryScope('all')
                    setActiveFilter('')
                  }}
                  dataAction='filter-catalog'
                  dataCatalogId={catalogId}
                >
                  All
                </Action>
                {orlando && (
                  <Action
                    variant='plain'
                    wrapContent={false}
                    className={!activeFilter && inventoryScope === 'orlando' ? styles.selected : ''}
                    ariaPressed={!activeFilter && inventoryScope === 'orlando'}
                    onClick={() => {
                      setInventoryScope('orlando')
                      setActiveFilter('')
                    }}
                    dataAction='filter-catalog'
                    dataCatalogId={catalogId}
                  >
                    Available in Orlando
                  </Action>
                )}
                {filterOptions.map(([label, value]) => (
                  <Action
                    variant='plain'
                    wrapContent={false}
                    key={value}
                    className={activeFilter === value ? styles.selected : ''}
                    ariaPressed={activeFilter === value}
                    onClick={() => setActiveFilter(value)}
                    dataAction='filter-catalog'
                    dataCatalogId={catalogId}
                  >
                    {label}
                  </Action>
                ))}
              </div>
            )}

            <label className={styles.sort}>
              <span>Sort by</span>
              <select value={sort} onChange={event => setSort(event.target.value)} data-action='sort-catalog' data-catalog-id={catalogId}>
                <option value='Relevance'>Most relevant</option>
                <option value='price-lh'>Price · low to high</option>
                <option value='price-hl'>Price · high to low</option>
                {sortOptions.map(option => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
          </div>
        </div>

        {showAssurances && (
          <aside className={styles.marketContext} aria-label={marketPresentation.ariaLabel}>
            <span className={styles.marketIcon} aria-hidden='true'><FiTruck /></span>
            <div>
              <span className={styles.marketEyebrow}>{marketPresentation.eyebrow}</span>
              <strong>{marketPresentation.heading}</strong>
              <p>{marketPresentation.description}</p>
            </div>
          </aside>
        )}

        {showAssurances && (
          <ul className={styles.assurances} aria-label='Rental benefits'>
            <li><FiTruck aria-hidden='true' /><span><strong>Delivery & setup</strong> available across {orlando ? 'Central Florida' : 'South Florida'}</span></li>
            <li><FiTag aria-hidden='true' /><span><strong>15–30% multi-day savings</strong> applied at checkout</span></li>
            <li><FiHeadphones aria-hidden='true' /><span><strong>AV team support</strong> before and during your rental</span></li>
          </ul>
        )}

        <div className={styles.resultsBar}>
          <p id={resultStatusId} role='status' aria-live='polite' aria-atomic='true'>
            Showing <strong>{firstVisibleItem}–{lastVisibleItem}</strong> of <strong>{filteredProducts.length}</strong> {safeListName.toLowerCase()} options
          </p>
          {priceSummaryMode === 'components-and-bundles' && (componentMinimumRentalPrice !== null || bundleMinimumRentalPrice !== null) ? (
            <span>
              {componentMinimumRentalPrice !== null && <>Components from <strong>{helper.formatPrice(componentMinimumRentalPrice)}</strong></>}
              {componentMinimumRentalPrice !== null && bundleMinimumRentalPrice !== null && <><span aria-hidden='true'> · </span><span className={styles.visuallyHidden}>; </span></>}
              {bundleMinimumRentalPrice !== null && <>Complete bundles from <strong>{helper.formatPrice(bundleMinimumRentalPrice)}</strong></>}
            </span>
          ) : minimumRentalPrice !== null && (
            <span>Rentals from <strong>{helper.formatPrice(minimumRentalPrice)}</strong> / 24 hours</span>
          )}
        </div>

        {pageProducts.length ? <div ref={productListRef} className={styles.list} aria-describedby={resultStatusId}>
          {pageProducts.map((product, index) => (
            <ProductRow
              key={product._id}
              product={product}
              addToCartItems={addToCartItems}
              openRentalStart={openRentalStart}
              unifiedEligible={unifiedEligible}
              activeFilter={activeFilter}
              catalogId={catalogId}
              listName={safeListName}
              listPosition={(page * normalizedPageSize) + index}
              resultCount={filteredProducts.length}
              sort={sort}
              actionMode={actionMode}
              supportingLabel={supportingLabel}
              supportingDescription={supportingDescription}
              supportingActionLabel={supportingActionLabel}
            />
          ))}
        </div> : (
          <div className={styles.empty} role='status'>
            <strong>{emptyState?.heading || 'No equipment matches this filter.'}</strong>
            <span>{emptyState?.description || 'Choose another filter to keep browsing.'}</span>
            {emptyState?.action ? <Action {...emptyState.action} size='compact'>
              {emptyState.action.label}
            </Action> : <Action
              variant='secondary'
              size='compact'
              onClick={() => {
                setActiveFilter('')
                setInventoryScope('all')
              }}
            >
              View all equipment
            </Action>}
          </div>
        )}

        {pages > 1 && (
          <nav className={styles.pagination} aria-label={`${safeListName} pages`}>
            <Action
              variant='secondary'
              size='compact'
              className={styles.pageDirection}
              disabled={page === 0}
              ariaLabel={`Previous ${safeListName} page`}
              onClick={() => selectPage(page - 1)}
            >
              Previous
            </Action>
            {visiblePageItems.map(item => typeof item === 'string'
              ? <span className={styles.ellipsis} aria-hidden='true' key={item}>…</span>
              : (
                <Action
                  variant='plain'
                  wrapContent={false}
                  className={styles.pageNumber}
                  key={item}
                  ariaCurrent={page === item ? 'page' : undefined}
                  ariaLabel={`${safeListName} page ${item + 1}`}
                  onClick={() => selectPage(item)}
                >
                  {item + 1}
                </Action>
                ))}
            <Action
              variant='secondary'
              size='compact'
              className={styles.pageDirection}
              disabled={page === pages - 1}
              ariaLabel={`Next ${safeListName} page`}
              onClick={() => selectPage(page + 1)}
            >
              Next
            </Action>
          </nav>
        )}

        {showProductDirectory && filteredProducts.length > normalizedPageSize && (
          <details className={styles.directory}>
            <summary>Browse all {filteredProducts.length} products in {safeListName}</summary>
            <p>Direct links to every product in this catalog.</p>
            <ul>
              {filteredProducts.map((product, index) => (
                <li key={product._id}>
                  <Action
                    href={`/products/${helper.makeUrl(product.name)}`}
                    variant='plain'
                    wrapContent={false}
                    dataAction='view-product-directory'
                    dataCatalogId={catalogId}
                    dataProductId={product._id}
                    onClick={() => trackDirectorySelection(product, index)}
                  >
                    {product.name}
                  </Action>
                </li>
              ))}
            </ul>
          </details>
        )}

        {carousel && (
          <CarouselJsonLd
            ofType='default'
            url={carousel.url}
            name={carousel.name}
            description={carousel.description}
            data={filteredProducts.map((product, index) => ({
              position: index + 1,
              type: 'Product',
              name: product.name,
              url: `https://miamisoundrental.com/products/${helper.makeUrl(product.name)}`,
              image: helper.findImage(product.picture),
              offers: {
                price: product.only_purchase ? product.price.map : product.price.rental,
                priceCurrency: 'USD'
              }
            }))}
          />
        )}
      </Container>
    </UiBoundary>
  )
}

export default ProductCatalog
